
import React from 'react';
import type { View } from '../App';
import { KharonLogo } from './Logo';

import { 
    HomeIcon, 
    CalendarIcon, 
    CasesIcon, 
    SettingsIcon,
    SidebarToggleIcon,
    ChartPieIcon
} from './icons';

const KharonSymbol: React.FC<{ className?: string }> = ({ className }) => (
    <svg id="Layer_2" data-name="Layer 2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 154.18 154.22" className={className} fill="currentColor">
        <g id="Capa_2" data-name="Capa 2">
            <path d="M112.1,14.5C102.75,5.15,90.31,0,77.09,0s-25.66,5.15-35.01,14.5l-27.6,27.6c-19.3,19.3-19.3,50.71,0,70.02l27.6,27.6c9.35,9.35,21.78,14.5,35.01,14.5s25.66-5.15,35.01-14.5l27.6-27.6c19.3-19.3,19.3-50.71,0-70.02l-27.6-27.6ZM118.49,90.91l-27.6,27.6c-3.81,3.81-8.8,5.71-13.79,5.71s-9.99-1.9-13.79-5.71l-27.6-27.6c-7.62-7.62-7.62-19.97,0-27.59l27.6-27.6c3.81-3.81,8.8-5.71,13.79-5.71s9.99,1.9,13.79,5.71l27.6,27.6c7.62,7.62,7.62,19.97,0,27.59Z"/>
        </g>
    </svg>
);

const NavItem: React.FC<{ icon: React.ReactNode; label: string; active?: boolean; onClick: () => void; isCollapsed: boolean; }> = ({ icon, label, active, onClick, isCollapsed }) => (
  <li className="mb-3 px-4">
    <button
        onClick={onClick}
        title={isCollapsed ? label : undefined}
        className={`group flex items-center w-full h-14 rounded-2xl transition-all duration-300 ${
            isCollapsed ? 'justify-center px-0' : 'px-5'
        } ${
            active 
            ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-luxury' 
            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
        }`}
    >
        <span className={`flex-shrink-0 transition-colors ${active ? 'text-primary-400 dark:text-primary-600' : 'text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300'}`}>
            {React.isValidElement(icon) ? React.cloneElement(icon as React.ReactElement<any>, { strokeWidth: active ? 2.5 : 2, className: "h-6 w-6" }) : icon}
        </span>
        {!isCollapsed && (
            <span className={`ml-4 whitespace-nowrap text-sm ${active ? 'font-bold' : 'font-medium'}`}>
                {label}
            </span>
        )}
    </button>
  </li>
);

export const Sidebar: React.FC<{
    activeView: View;
    onNavigate: (view: View) => void;
    isCollapsed: boolean;
    setCollapsed: (collapsed: boolean) => void;
    isDarkMode: boolean; // Note: Kept for consistent props if needed later
    userRole: 'admin' | 'user';
}> = ({ activeView, onNavigate, isCollapsed, setCollapsed, userRole }) => {
    return (
        <aside className={`hidden md:flex flex-col bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] z-40 ${isCollapsed ? 'w-20' : 'w-72'}`}>
            <div className={`flex items-center h-24 flex-shrink-0 overflow-hidden transition-all duration-500 mt-5 ${isCollapsed ? 'justify-center px-0' : 'justify-center px-6'}`}>
                {isCollapsed ? <KharonSymbol className="h-8 w-8 text-slate-900 dark:text-white" /> : <KharonLogo className="h-8 w-auto text-slate-900 dark:text-white" />}
            </div>
            
            <nav className="flex-grow mt-8">
                <ul className="space-y-1">
                    <NavItem icon={<HomeIcon />} label="Inicio" active={activeView === 'dashboard'} onClick={() => onNavigate('dashboard')} isCollapsed={isCollapsed} />
                    <NavItem icon={<CalendarIcon />} label="Calendario" active={activeView === 'calendar'} onClick={() => onNavigate('calendar')} isCollapsed={isCollapsed} />
                    <NavItem icon={<CasesIcon />} label="Expedientes" active={activeView === 'cases'} onClick={() => onNavigate('cases')} isCollapsed={isCollapsed} />
                    <NavItem icon={<ChartPieIcon />} label="Finanzas" active={activeView === 'analytics'} onClick={() => onNavigate('analytics')} isCollapsed={isCollapsed} />
                    <NavItem icon={<SettingsIcon />} label="Ajustes" active={activeView === 'settings'} onClick={() => onNavigate('settings')} isCollapsed={isCollapsed} />
                </ul>
            </nav>

            <div className="p-4 border-t border-slate-50 dark:border-slate-800/50 mt-auto">
                <button
                    onClick={() => setCollapsed(!isCollapsed)}
                    className={`group flex items-center w-full h-14 rounded-2xl transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-800 ${isCollapsed ? 'justify-center px-0' : 'px-5'}`}
                >
                    <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 group-hover:text-primary-500 transition-all">
                        <SidebarToggleIcon isCollapsed={isCollapsed} />
                    </div>
                    {!isCollapsed && <span className="ml-4 whitespace-nowrap text-sm font-bold text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">Cerrar Menú</span>}
                </button>
            </div>
        </aside>
    );
};

export const BottomNav: React.FC<{
    activeView: View;
    onNavigate: (view: View) => void;
    userRole: 'admin' | 'user';
}> = ({ activeView, onNavigate }) => {
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 w-full h-[88px] pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 z-50 flex justify-around items-center px-4 safe-area-bottom">
        <button onClick={() => onNavigate('dashboard')} className={`flex flex-col items-center justify-center w-full h-full ${activeView === 'dashboard' ? 'text-primary-500' : 'text-slate-400'}`}><HomeIcon /></button>
        <button onClick={() => onNavigate('calendar')} className={`flex flex-col items-center justify-center w-full h-full ${activeView === 'calendar' ? 'text-primary-500' : 'text-slate-400'}`}><CalendarIcon /></button>
        <button onClick={() => onNavigate('cases')} className={`flex flex-col items-center justify-center w-full h-full ${activeView === 'cases' ? 'text-primary-500' : 'text-slate-400'}`}><CasesIcon /></button>
        <button onClick={() => onNavigate('analytics')} className={`flex flex-col items-center justify-center w-full h-full ${activeView === 'analytics' ? 'text-primary-500' : 'text-slate-400'}`}><ChartPieIcon /></button>
        <button onClick={() => onNavigate('settings')} className={`flex flex-col items-center justify-center w-full h-full ${activeView === 'settings' ? 'text-primary-500' : 'text-slate-400'}`}><SettingsIcon /></button>
    </nav>
  );
};
